import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Sparkles, Gift, Coffee, Send, Check, Heart, Cake, RefreshCw, Wand2, Mail, MessageSquare, Link2, Copy, Share2, QrCode, Calendar, Clock, Bell, Eye, Users, ChevronRight } from 'lucide-react';
import { useParams, useLocation } from 'react-router-dom';
import confetti from 'canvas-confetti';

interface StepData {
  recipientName?: string;
  birthMonth?: number;
  birthDay?: number;
  deliveryMethod?: 'email' | 'sms' | 'whatsapp' | 'link';
  recipientContact?: string;
  contactInfo?: string;
  senderName?: string;
  message?: string;
  includesCoffee?: boolean;
  coffeeAmount?: number;
  scheduleDelivery?: boolean;
  scheduledDate?: string;
}

// Belated birthday messages
const belatedMessages = [
  "Happy Belated Birthday! 🎂 Sorry I'm late to the party, but my wishes for your happiness are right on time! Hope your day was amazing!",
  "Better late than never! 🎉 Happy Belated Birthday! I hope your special day was filled with love, laughter, and cake!",
  "Oops, I'm fashionably late! 😊 Happy Belated Birthday! May this year bring you everything you wished for and more!",
  "Happy Belated Birthday! The best things are worth waiting for, including my birthday wishes! Hope it was wonderful!",
  "Sorry I missed your big day! 🎈 Happy Belated Birthday! Here's to celebrating you all year long!",
];

// AI-powered birthday messages
const birthdayMessages = [
  "Happy Birthday! 🎉 Wishing you a day filled with joy, laughter, and all your favorite things. May this year bring you endless happiness and exciting adventures!",
  "Another year older, another year wiser, and definitely another year more awesome! 🎂 Hope your special day is as wonderful as you are!",
  "Birthdays are nature's way of telling us to eat more cake! 🍰 Wishing you the happiest of birthdays and a year filled with blessings and joy!",
  "Happy Birthday to someone who makes the world a brighter place just by being in it! ✨ May all your birthday wishes come true!",
  "Cheers to you on your special day! 🥳 May this birthday be the beginning of your best year yet. Sending you lots of love and warm wishes!",
  "Happy Birthday! May your day be filled with sunshine, rainbows, laughter, and everything else that makes you happy! 🌈",
  "Wishing you a birthday that's as special as you are! May this year bring you closer to your dreams and fill your life with joy! 🎈",
  "It's your special day! Time to celebrate the amazing person you are. Happy Birthday and here's to many more! 🎊",
  "Roses are red, violets are blue, there's no one quite as special as you! Happy Birthday! 🌹 Hope your day is fantastic!",
  "Happy Birthday! Remember, age is just a number... a really, really big number in your case! 😄 Just kidding - you're aging like fine wine!",
  "On your birthday, I wanted to give you something funny and charming, but then I remembered you already have me in your life! 😊 Happy Birthday!",
  "Happy Birthday to someone who's forever young at heart! May your day be filled with love, laughter, and lots of cake! 🎂",
  "Sending you smiles for every moment of your special day! Have a wonderful time and a very Happy Birthday! 😊",
  "May your birthday be the start of a year filled with good luck, good health, and much happiness. Wishing you all the best!",
  "Happy Birthday! Today is all about you, so live it up and enjoy every moment! You deserve all the happiness in the world! 💝"
];

const StepByStepBirthdayFlow: React.FC = () => {
  const params = useParams<{ template?: string; name?: string }>();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState<StepData>({});
  const [currentInput, setCurrentInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState<any>(null);

  // Check for devMode
  const isDevMode = new URLSearchParams(window.location.search).get('devMode') === 'true';

  // Check if this is a shared card view
  const isSharedCard = location.pathname.startsWith('/card/') || location.pathname === '/cards';

  // Handle shared card view
  React.useEffect(() => {
    if (isSharedCard && params?.name) {
      // Set up the card preview with the name from the URL
      setData({
        recipientName: params.name,
        senderName: 'A Friend',
        message: birthdayMessages[Math.floor(Math.random() * birthdayMessages.length)],
        includesCoffee: true,
        coffeeAmount: 15
      });
    }
  }, [isSharedCard, params]);

  // Check for quick send contact from Birthday Hub
  React.useEffect(() => {
    const quickSend = localStorage.getItem('quickSendContact');
    if (quickSend) {
      const contact = JSON.parse(quickSend);
      const [year, month, day] = contact.birthday.split('-').map(Number);
      setData({
        recipientName: contact.name,
        birthMonth: month,
        birthDay: day,
        recipientContact: contact.email || contact.phone || '',
        contactInfo: contact.email || contact.phone || ''
      });
      setCurrentInput(contact.name);
      localStorage.removeItem('quickSendContact');
    }
  }, []);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [hasUsedSuggestion, setHasUsedSuggestion] = useState(false);

  const [selectedDelivery, setSelectedDelivery] = useState<'email' | 'sms' | 'whatsapp' | 'link' | null>(null);
  const [shareLink, setShareLink] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedDay, setSelectedDay] = useState<number>(new Date().getDate());
  const [birthdayStatus, setBirthdayStatus] = useState<'today' | 'upcoming' | 'belated'>('today');
  const [wantsToSchedule, setWantsToSchedule] = useState(false);

  const months = ['January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December'];

  const messages = [
    "Who's birthday is it? 🎂",
    "When's their birthday? 📅",
    "How do you want to reach them? 📬",
    selectedDelivery === 'email' ? "What's their email? 📧" :
    selectedDelivery === 'sms' ? "What's their phone number? 📱" :
    selectedDelivery === 'whatsapp' ? "What's their WhatsApp number? 💬" :
    "Creating your share link... 🔗",
    "And you are...? ✨",
    "Write something special 💝",
    "Want to add coffee? ☕",
    "Ready to spread joy? 🎉"
  ];

  const placeholders = [
    "Birthday friend's name",
    "",
    selectedDelivery === 'email' ? "their.email@example.com" :
    selectedDelivery === 'sms' || selectedDelivery === 'whatsapp' ? "+1 234 567 8900" :
    "",
    "Your name",
    "Write your own message or use our suggestions below...",
    "",
    ""
  ];

  // Check birthday status
  const checkBirthdayStatus = (month: number, day: number) => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const birthday = new Date(currentYear, month - 1, day);
    const daysDiff = Math.floor((birthday.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (daysDiff === 0) {
      return 'today';
    } else if (daysDiff < 0 && daysDiff >= -30) {
      return 'belated';
    } else if (daysDiff > 0 && daysDiff <= 365) {
      return 'upcoming';
    } else if (daysDiff < -30) {
      // If more than 30 days ago, consider it for next year
      return 'upcoming';
    }
    return 'today';
  };

  // Generate personalized message with recipient's name
  const getPersonalizedMessage = (message: string) => {
    if (data.recipientName) {
      // For belated messages
      if (message.includes('Happy Belated Birthday!')) {
        return message.replace('Happy Belated Birthday!', `Happy Belated Birthday, ${data.recipientName}!`);
      }
      // For regular messages
      return message.replace('Happy Birthday!', `Happy Birthday, ${data.recipientName}!`);
    }
    return message;
  };

  // Use suggested message
  const useSuggestedMessage = () => {
    const messageArray = birthdayStatus === 'belated' ? belatedMessages : birthdayMessages;
    const personalizedMessage = getPersonalizedMessage(messageArray[currentMessageIndex]);
    setCurrentInput(personalizedMessage);
    setHasUsedSuggestion(true);
  };

  // Get next message suggestion
  const nextSuggestion = () => {
    const messageArray = birthdayStatus === 'belated' ? belatedMessages : birthdayMessages;
    setCurrentMessageIndex((prev) => (prev + 1) % messageArray.length);
  };

  // Get previous message suggestion
  const previousSuggestion = () => {
    const messageArray = birthdayStatus === 'belated' ? belatedMessages : birthdayMessages;
    setCurrentMessageIndex((prev) => (prev - 1 + messageArray.length) % messageArray.length);
  };

  // Reset to step 5 when we reach it (message step)
  useEffect(() => {
    if (currentStep === 5) {
      setCurrentMessageIndex(Math.floor(Math.random() * birthdayMessages.length));
      setHasUsedSuggestion(false);
    }
  }, [currentStep]);

  // Copy share link
  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleNext = () => {
    // Save current step data
    switch (currentStep) {
      case 1:
        if (!currentInput.trim()) return;
        setData({ ...data, recipientName: currentInput });
        break;
      case 2:
        // Delivery method is handled separately
        break;
      case 3:
        if (selectedDelivery === 'link') {
          // Skip contact info for link sharing
          setCurrentStep(4);
          setCurrentInput('');
          return;
        }
        if (!currentInput.trim()) return;
        setData({ ...data, recipientContact: currentInput, contactInfo: currentInput });
        break;
      case 4:
        if (!currentInput.trim()) return;
        setData({ ...data, senderName: currentInput });
        break;
      case 5:
        if (!currentInput.trim()) return;
        setData({ ...data, message: currentInput });
        break;
    }

    // Move to next step
    if (currentStep < 7) {
      setCurrentStep(currentStep + 1);
      setCurrentInput('');
    }
  };

  const handleDeliverySelect = (method: 'email' | 'sms' | 'whatsapp' | 'link') => {
    setSelectedDelivery(method);
    setData({ ...data, deliveryMethod: method });

    if (method === 'link') {
      // Generate share link immediately with template and name
      const recipientName = data.recipientName || 'Friend';
      const nameSlug = recipientName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      setShareLink(`birthday.coffee/card/coffee-blast/${recipientName}`);
      setCurrentStep(3.5); // Show link screen
    } else {
      setCurrentStep(3); // Go to contact info
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && currentStep <= 4) {
      e.preventDefault();
      handleNext();
    }
  };

  const handleSend = async () => {
    setIsSending(true);
    setSendResult(null);

    try {
      const response = await fetch('https://birthday-coffee-api.b-9f2.workers.dev/api/cards/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          recipientEmail: data.deliveryMethod === 'email' ? data.recipientContact : `${(data.recipientName || 'friend').replace(/\s/g, '').toLowerCase()}@birthday.coffee`,
          cardDesign: 'confetti-blast',
          includesCoffee: data.includesCoffee || false,
          coffeeAmount: data.coffeeAmount || 0
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSendResult(result);
        // Celebration!
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
      } else {
        setSendResult({ success: false, error: result.error });
      }
    } catch (error) {
      setSendResult({ success: false, error: 'Failed to send card' });
    } finally {
      setIsSending(false);
    }
  };

  const handleCoffeeToggle = (include: boolean) => {
    setData({ ...data, includesCoffee: include });
    if (include) {
      setCurrentStep(7.5); // Show amount selection
    } else {
      setCurrentStep(8); // Go to preview
    }
  };

  const handleCoffeeAmount = (amount: number) => {
    setData({ ...data, coffeeAmount: amount });
    setCurrentStep(8); // Go to preview
  };

  const resetFlow = () => {
    setCurrentStep(1);
    setData({});
    setCurrentInput('');
    setSendResult(null);
  };

  if (sendResult?.success) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 flex items-center justify-center p-4"
      >
        <div className="bg-white rounded-3xl shadow-2xl p-12 max-w-md w-full text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <Check className="w-10 h-10 text-green-600" />
          </motion.div>

          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            {data.deliveryMethod === 'link'
              ? 'Birthday Card Ready! 🎉'
              : data.scheduleDelivery
                ? 'Birthday Card Scheduled! 📅'
                : birthdayStatus === 'belated'
                  ? 'Belated Wishes Sent! 🎂'
                  : birthdayStatus === 'upcoming' && !data.scheduleDelivery
                    ? 'Early Birthday Wishes Sent! 🎁'
                    : 'Birthday Joy Sent! 🎉'}
          </h2>

          <p className="text-gray-600 mb-6">
            {data.deliveryMethod === 'link'
              ? `Your birthday card for ${data.recipientName} is ready to share!`
              : data.scheduleDelivery
                ? `${data.recipientName} will receive your birthday wishes on ${months[(data.birthMonth || 1) - 1]} ${data.birthDay}!`
                : birthdayStatus === 'belated'
                  ? `${data.recipientName} will receive your belated birthday wishes!`
                  : `${data.recipientName} will receive your birthday wishes!`
            }
          </p>

          {data.scheduleDelivery && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-purple-50 rounded-xl p-4 mb-6 flex items-center justify-center gap-3"
            >
              <Bell className="w-5 h-5 text-purple-600" />
              <span className="text-purple-700 font-medium">Reminder set for {months[(data.birthMonth || 1) - 1]} {data.birthDay}</span>
            </motion.div>
          )}

          {data.deliveryMethod === 'link' ? (
            <>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-6">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Link2 className="w-5 h-5 text-blue-600" />
                  <p className="text-sm font-semibold text-blue-700">Your Shareable Birthday Card Link</p>
                </div>
                <div className="bg-white rounded-xl p-3 flex items-center gap-2">
                  <input
                    type="text"
                    value={sendResult.cardUrl}
                    readOnly
                    className="flex-1 text-sm text-gray-700 bg-transparent outline-none font-mono"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(sendResult.cardUrl);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center gap-2"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-6">
                <button
                  onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Check out this birthday card I made for you! 🎂 ${sendResult.cardUrl}`)}`, '_blank')}
                  className="p-3 bg-green-50 rounded-xl border border-green-200 hover:bg-green-100 transition-colors flex items-center justify-center gap-2"
                >
                  <MessageSquare className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium">Share on WhatsApp</span>
                </button>

                <button
                  onClick={() => window.open(`mailto:?subject=Happy Birthday!&body=${encodeURIComponent(`I made you a special birthday card! 🎉\n\nCheck it out here: ${sendResult.cardUrl}`)}`, '_blank')}
                  className="p-3 bg-blue-50 rounded-xl border border-blue-200 hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
                >
                  <Mail className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium">Share via Email</span>
                </button>
              </div>
            </>
          ) : (
            <div className="bg-amber-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-amber-700 mb-2">Card URL:</p>
              <p className="text-xs text-amber-900 font-mono break-all">
                {sendResult.cardUrl}
              </p>
            </div>
          )}

          <button
            onClick={resetFlow}
            className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
          >
            Create Another Card
          </button>

          {/* Subtle hub discovery */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 3 }}
            className="mt-8 pt-8 border-t border-gray-200"
          >
            <p className="text-gray-600 mb-3">Want to remember everyone's birthday?</p>
            <a
              href="/hub"
              className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium transition-colors"
            >
              <Users className="w-4 h-4" />
              View Birthday Hub
              <ChevronRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  // Show card preview if this is a shared card link
  if (isSharedCard && params?.name) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl font-bold mb-2 text-orange-800">Animated Birthday Cards</h1>
            <p className="text-lg text-orange-600 mb-8">Send a card that's worth sharing on social media</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-3xl shadow-2xl overflow-hidden p-8"
          >
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Card Preview</h2>

            {/* Birthday Card Preview */}
            <div className="relative bg-gradient-to-br from-amber-400 via-orange-400 to-rose-400 rounded-2xl p-8 shadow-xl overflow-hidden">
              <div className="relative z-10 text-center text-white">
                <div className="flex justify-center items-center gap-2 mb-4">
                  <Coffee className="w-12 h-12" />
                  <Sparkles className="w-8 h-8 text-yellow-300 animate-pulse" />
                </div>
                <h2 className="text-3xl font-bold mb-2">
                  Happy Birthday, {params.name}!
                </h2>
                <p className="text-xl mb-6 opacity-90">
                  Wishing you a brew-tiful birthday! ☕
                </p>
                {data.includesCoffee && (
                  <div className="bg-white/20 backdrop-blur rounded-xl px-6 py-3 inline-block">
                    <p className="font-semibold">☕ ${data.coffeeAmount} Coffee Gift Included!</p>
                  </div>
                )}
                <p className="mt-6 text-lg opacity-90">
                  From: {data.senderName || 'Alex'}
                </p>
              </div>

              {/* Decorative elements */}
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="absolute text-6xl opacity-20 animate-float"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 5}s`,
                    animationDuration: `${5 + Math.random() * 5}s`
                  }}
                >
                  {['🎂', '☕', '🎉', '🎈', '⭐', '✨', '🎊', '🎁'][i % 8]}
                </div>
              ))}
            </div>

            <div className="mt-8 space-y-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-gray-900 text-white py-4 px-6 rounded-xl font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-3"
                onClick={() => {
                  const tweetText = `Happy Birthday, ${params.name}! 🎉🎂☕\nCheck out your birthday card: ${window.location.href}`;
                  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`, '_blank');
                }}
              >
                <Share2 className="w-5 h-5" />
                Share on Twitter
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-white text-gray-800 py-4 px-6 rounded-xl font-medium border-2 border-gray-200 hover:bg-gray-50 transition-colors"
                onClick={() => window.location.href = '/'}
              >
                <Gift className="w-5 h-5 inline mr-2" />
                Create Your Own Card
              </motion.button>
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-600 mb-2 font-medium">Share link:</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={window.location.href}
                  readOnly
                  className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-sm font-medium"
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-8">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((step) => (
            <motion.div
              key={step}
              className={`h-2 rounded-full transition-all duration-300 ${
                step <= Math.floor(currentStep)
                  ? 'w-8 bg-amber-500'
                  : 'w-2 bg-amber-200'
              }`}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: step * 0.1 }}
            />
          ))}
        </div>

        {/* Main card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-2xl overflow-hidden"
        >
          {/* Dev Mode Indicator */}
          {isDevMode && (
            <div className="bg-gray-800 text-white px-4 py-2 text-center font-mono text-sm">
              🛠️ Dev Mode | Step: {currentStep} | Data: {JSON.stringify(data).substring(0, 100)}...
            </div>
          )}

          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-8 text-center">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="text-6xl mb-4">
                {currentStep === 1 && '🎂'}
                {currentStep === 2 && '📅'}
                {currentStep === 3 && '📬'}
                {currentStep === 4 && (selectedDelivery === 'email' ? '📧' : selectedDelivery === 'sms' ? '📱' : selectedDelivery === 'whatsapp' ? '💬' : '🔗')}
                {currentStep === 4.5 && '🔗'}
                {currentStep === 5 && '✨'}
                {currentStep === 6 && '💝'}
                {currentStep === 7 && '☕'}
                {currentStep === 7.5 && '💰'}
                {currentStep === 8 && '🎉'}
              </div>
              <h2 className="text-2xl font-bold text-white">
                {currentStep === 1 ? messages[0] :
                 currentStep === 2 ? messages[1] :
                 currentStep === 3 && !selectedDelivery ? messages[2] :
                 currentStep === 3 && selectedDelivery ? messages[3] :
                 currentStep === 4 ? messages[4] :
                 currentStep === 5 ? messages[5] :
                 currentStep === 6 ? messages[6] :
                 ''}
              </h2>
            </motion.div>
          </div>

          {/* Content */}
          <div className="p-8">
            <AnimatePresence mode="wait">
              {/* Step 2: Birthday date selection */}
              {currentStep === 2 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="space-y-6"
                >
                  <div className="grid grid-cols-2 gap-4">
                    {/* Month selector */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
                      <select
                        value={selectedMonth}
                        onChange={(e) => {
                          const month = parseInt(e.target.value);
                          setSelectedMonth(month);
                          setData({ ...data, birthMonth: month });
                          const status = checkBirthdayStatus(month, selectedDay);
                          setBirthdayStatus(status);
                        }}
                        className="w-full px-4 py-3 border-2 border-amber-200 rounded-xl focus:border-amber-400 focus:outline-none text-lg"
                      >
                        {months.map((month, index) => (
                          <option key={month} value={index + 1}>{month}</option>
                        ))}
                      </select>
                    </div>

                    {/* Day selector */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Day</label>
                      <select
                        value={selectedDay}
                        onChange={(e) => {
                          const day = parseInt(e.target.value);
                          setSelectedDay(day);
                          setData({ ...data, birthDay: day });
                          const status = checkBirthdayStatus(selectedMonth, day);
                          setBirthdayStatus(status);
                        }}
                        className="w-full px-4 py-3 border-2 border-amber-200 rounded-xl focus:border-amber-400 focus:outline-none text-lg"
                      >
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                          <option key={day} value={day}>{day}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Birthday status indicator */}
                  {birthdayStatus === 'today' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 text-center"
                    >
                      <div className="flex items-center justify-center gap-2 text-green-700">
                        <Cake className="w-5 h-5" />
                        <p className="font-semibold">It's their birthday today! 🎉</p>
                      </div>
                      <p className="text-sm text-green-600 mt-1">Perfect timing for birthday wishes!</p>
                    </motion.div>
                  )}

                  {birthdayStatus === 'belated' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 text-center"
                    >
                      <div className="flex items-center justify-center gap-2 text-orange-700">
                        <Clock className="w-5 h-5" />
                        <p className="font-semibold">Oops, you're a bit late! 📅</p>
                      </div>
                      <p className="text-sm text-orange-600 mt-1">No worries - we'll help you send belated wishes!</p>
                    </motion.div>
                  )}

                  {birthdayStatus === 'upcoming' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4"
                    >
                      <div className="flex items-center justify-center gap-2 text-purple-700">
                        <Calendar className="w-5 h-5" />
                        <p className="font-semibold">Birthday coming up!</p>
                      </div>
                      <p className="text-sm text-purple-600 mt-2 text-center">
                        Would you like to schedule this card to be sent on their birthday?
                      </p>
                      <div className="grid grid-cols-2 gap-3 mt-4">
                        <button
                          onClick={() => {
                            setWantsToSchedule(true);
                            setData({ ...data, scheduleDelivery: true });
                            setCurrentStep(3);
                          }}
                          className="p-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                        >
                          <Bell className="w-4 h-4" />
                          <span className="text-sm font-medium">Schedule it</span>
                        </button>
                        <button
                          onClick={() => {
                            setWantsToSchedule(false);
                            setData({ ...data, scheduleDelivery: false });
                            setCurrentStep(3);
                          }}
                          className="p-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <span className="text-sm font-medium">Send now</span>
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {(birthdayStatus === 'today' || birthdayStatus === 'belated') && (
                    <button
                      onClick={() => setCurrentStep(3)}
                      className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-3"
                    >
                      Continue
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  )}
                </motion.div>
              )}

              {/* Step 3: Delivery method selection */}
              {currentStep === 3 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleDeliverySelect('email')}
                      className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-200 hover:border-blue-400 transition-all group"
                    >
                      <Mail className="w-8 h-8 text-blue-600 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                      <p className="font-semibold text-blue-800">Email</p>
                      <p className="text-xs text-blue-600 mt-1">Send via email</p>
                    </button>

                    <button
                      onClick={() => handleDeliverySelect('sms')}
                      className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border-2 border-green-200 hover:border-green-400 transition-all group"
                    >
                      <MessageSquare className="w-8 h-8 text-green-600 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                      <p className="font-semibold text-green-800">Text Message</p>
                      <p className="text-xs text-green-600 mt-1">Send via SMS</p>
                    </button>

                    <button
                      onClick={() => handleDeliverySelect('whatsapp')}
                      className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border-2 border-emerald-200 hover:border-emerald-400 transition-all group"
                    >
                      <MessageSquare className="w-8 h-8 text-emerald-600 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                      <p className="font-semibold text-emerald-800">WhatsApp</p>
                      <p className="text-xs text-emerald-600 mt-1">Send via WhatsApp</p>
                    </button>

                    <button
                      onClick={() => handleDeliverySelect('link')}
                      className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border-2 border-purple-200 hover:border-purple-400 transition-all group"
                    >
                      <Link2 className="w-8 h-8 text-purple-600 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                      <p className="font-semibold text-purple-800">Share Link</p>
                      <p className="text-xs text-purple-600 mt-1">Get a link to share</p>
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Step 3.5: Share link display */}
              {currentStep === 3.5 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="space-y-6"
                >
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 text-center">
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <Link2 className="w-6 h-6 text-blue-600" />
                      <h3 className="font-semibold text-blue-800">Your Birthday Card Link</h3>
                    </div>

                    <div className="bg-white rounded-xl p-3 flex items-center gap-2 mb-4">
                      <input
                        type="text"
                        value={shareLink}
                        readOnly
                        className="flex-1 text-sm text-gray-700 bg-transparent outline-none"
                      />
                      <button
                        onClick={copyShareLink}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>

                    <p className="text-sm text-gray-600">
                      Share this link with {data.recipientName} however you like!
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => window.open(`mailto:?subject=Happy Birthday!&body=Check out your birthday card: ${shareLink}`, '_blank')}
                      className="p-3 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
                    >
                      <Mail className="w-5 h-5 text-gray-600" />
                      <span className="text-sm font-medium">Email Link</span>
                    </button>

                    <button
                      onClick={() => window.open(`https://wa.me/?text=Check out your birthday card: ${shareLink}`, '_blank')}
                      className="p-3 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
                    >
                      <MessageSquare className="w-5 h-5 text-gray-600" />
                      <span className="text-sm font-medium">WhatsApp</span>
                    </button>
                  </div>

                  <button
                    onClick={() => setCurrentStep(4)}
                    className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-3"
                  >
                    Continue to Next Step
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </motion.div>
              )}

              {/* Steps 1, 3-5: Text inputs */}
              {(currentStep === 1 || (currentStep >= 3 && currentStep <= 5)) && currentStep !== 3.5 && (
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                >
                  {currentStep === 5 ? (
                    <div className="space-y-4">
                      <textarea
                        value={currentInput}
                        onChange={(e) => {
                          setCurrentInput(e.target.value);
                          setHasUsedSuggestion(false);
                        }}
                        onKeyPress={handleKeyPress}
                        placeholder={placeholders[currentStep - 1]}
                        className="w-full px-6 py-4 text-lg border-2 border-amber-200 rounded-2xl focus:border-amber-400 focus:outline-none transition-colors resize-none h-32"
                        autoFocus
                      />

                      {/* AI Suggestions */}
                      {!hasUsedSuggestion && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-4 border border-purple-200"
                        >
                          <div className="flex items-center gap-2 mb-3">
                            <Wand2 className="w-5 h-5 text-purple-600" />
                            <span className="text-sm font-semibold text-purple-700">
                              {birthdayStatus === 'belated' ? 'AI Belated Birthday Message' :
                               birthdayStatus === 'upcoming' && wantsToSchedule ? 'AI Birthday Message (Scheduled)' :
                               'AI Birthday Message Suggestion'}
                            </span>
                          </div>

                          <p className="text-sm text-gray-700 mb-3 italic">
                            "{getPersonalizedMessage((birthdayStatus === 'belated' ? belatedMessages : birthdayMessages)[currentMessageIndex])}"
                          </p>

                          <div className="flex gap-2">
                            <button
                              onClick={useSuggestedMessage}
                              className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
                            >
                              Use This Message
                            </button>
                            <button
                              onClick={previousSuggestion}
                              className="px-3 py-2 bg-white border border-purple-300 rounded-lg hover:bg-purple-50 transition-colors"
                              title="Previous suggestion"
                            >
                              ←
                            </button>
                            <button
                              onClick={nextSuggestion}
                              className="px-3 py-2 bg-white border border-purple-300 rounded-lg hover:bg-purple-50 transition-colors"
                              title="Next suggestion"
                            >
                              →
                            </button>
                          </div>
                        </motion.div>
                      )}

                      {/* Show when user has used a suggestion */}
                      {hasUsedSuggestion && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="flex items-center gap-2 text-sm text-purple-600 bg-purple-50 px-3 py-2 rounded-lg"
                        >
                          <Check className="w-4 h-4" />
                          <span>Using AI-suggested message (feel free to edit!)</span>
                        </motion.div>
                      )}
                    </div>
                  ) : (
                    <input
                      type={
                        currentStep === 3 && selectedDelivery === 'email' ? "email" :
                        currentStep === 3 && (selectedDelivery === 'sms' || selectedDelivery === 'whatsapp') ? "tel" :
                        "text"
                      }
                      value={currentInput}
                      onChange={(e) => setCurrentInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={
                        currentStep === 1 ? placeholders[0] :
                        currentStep === 3 ? (
                          selectedDelivery === 'email' ? "their.email@example.com" :
                          selectedDelivery === 'sms' || selectedDelivery === 'whatsapp' ? "+1 234 567 8900" :
                          ""
                        ) :
                        currentStep === 4 ? "Your name" :
                        placeholders[currentStep - 1]
                      }
                      className="w-full px-6 py-4 text-lg border-2 border-amber-200 rounded-2xl focus:border-amber-400 focus:outline-none transition-colors"
                      autoFocus
                    />
                  )}

                  <div className="flex gap-3 mt-6">
                    {currentStep > 1 && (
                      <button
                        onClick={() => {
                          setCurrentStep(currentStep - 1);
                          setCurrentInput('');
                        }}
                        className="px-6 py-4 rounded-2xl font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all duration-200 flex items-center justify-center gap-2"
                      >
                        <ArrowLeft className="w-5 h-5" />
                        Back
                      </button>
                    )}
                    <button
                      onClick={handleNext}
                      disabled={!currentInput.trim()}
                      className={`flex-1 py-4 rounded-2xl font-semibold text-white transition-all duration-200 flex items-center justify-center gap-3 ${
                        currentInput.trim()
                          ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:shadow-xl transform hover:-translate-y-0.5'
                          : 'bg-gray-300 cursor-not-allowed'
                      }`}
                    >
                      Continue
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Step 6: Coffee option */}
              {currentStep === 6 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="text-center"
                >
                  <p className="text-gray-600 mb-8">
                    Want to include a coffee gift with your birthday card?
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => handleCoffeeToggle(true)}
                      className="p-6 bg-gradient-to-r from-amber-100 to-orange-100 rounded-2xl border-2 border-amber-300 hover:border-amber-400 transition-all group"
                    >
                      <Coffee className="w-8 h-8 text-amber-600 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                      <p className="font-semibold text-amber-800">Yes, add coffee!</p>
                      <p className="text-sm text-amber-600 mt-1">Make it extra special</p>
                    </button>
                    <button
                      onClick={() => handleCoffeeToggle(false)}
                      className="p-6 bg-gray-50 rounded-2xl border-2 border-gray-200 hover:border-gray-300 transition-all group"
                    >
                      <Cake className="w-8 h-8 text-gray-500 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                      <p className="font-semibold text-gray-700">Just the card</p>
                      <p className="text-sm text-gray-500 mt-1">Birthday wishes only</p>
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Step 6.5: Coffee amount */}
              {currentStep === 6.5 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="text-center space-y-6"
                >
                  <div>
                    <p className="text-gray-600 mb-2">
                      How much coffee love? ☕
                    </p>
                    <p className="text-sm text-gray-500">
                      Choose a gift amount
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto">
                    {[5, 10, 15, 25].map((amount) => (
                      <button
                        key={amount}
                        onClick={() => handleCoffeeAmount(amount)}
                        className="relative p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-200 hover:border-amber-400 hover:scale-105 transition-all shadow-lg hover:shadow-xl group"
                      >
                        <div className="absolute top-2 right-2 text-2xl opacity-50 group-hover:opacity-100 transition-opacity">
                          ☕
                        </div>
                        <p className="text-3xl font-bold text-amber-800 mb-1">${amount}</p>
                        <p className="text-xs text-amber-600">Gift Card</p>
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      setData({ ...data, includesCoffee: false, coffeeAmount: undefined });
                      setCurrentStep(8);
                    }}
                    className="text-gray-500 hover:text-gray-700 text-sm underline"
                  >
                    Skip coffee gift
                  </button>
                </motion.div>
              )}

              {/* Step 8: Preview Card */}
              {currentStep === 8 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 text-center">
                    <div className="text-6xl mb-4">🎂</div>
                    <h2 className="text-3xl font-bold text-gray-800 mb-2">
                      {birthdayStatus === 'belated'
                        ? `Happy Belated Birthday, ${data.recipientName}!`
                        : `Happy Birthday, ${data.recipientName}!`}
                    </h2>
                    <p className="text-gray-600 text-lg mb-6">From {data.senderName}</p>

                    <div className="bg-white rounded-xl p-6 shadow-inner">
                      <p className="text-gray-700 text-lg italic">{data.message}</p>
                    </div>

                    {data.includesCoffee && (
                      <div className="mt-6 inline-flex items-center gap-3 bg-gradient-to-r from-amber-100 to-orange-100 px-6 py-3 rounded-full">
                        <Coffee className="w-6 h-6 text-amber-600" />
                        <span className="font-semibold text-amber-800">Includes ${data.coffeeAmount} Coffee Gift</span>
                      </div>
                    )}

                    {wantsToSchedule && (
                      <div className="mt-4 inline-flex items-center gap-2 text-purple-600">
                        <Bell className="w-5 h-5" />
                        <span className="text-sm">Will be sent on {months[(data.birthMonth || 1) - 1]} {data.birthDay}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setCurrentStep(currentStep - 1)}
                      className="px-6 py-4 rounded-2xl font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all duration-200 flex items-center justify-center gap-2"
                    >
                      <ArrowLeft className="w-5 h-5" />
                      Back to Edit
                    </button>
                    <button
                      onClick={() => setCurrentStep(9)}
                      className="flex-1 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-3"
                    >
                      <Eye className="w-5 h-5" />
                      Looks Good! Continue
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Step 9: Review and send */}
              {currentStep === 9 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6">
                    <h3 className="font-semibold text-amber-800 mb-3">
                      {data.deliveryMethod === 'link' ? 'Ready to create:' : 'Ready to send:'}
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p className="text-gray-700">
                        <span className="font-medium">For:</span> {data.recipientName}
                      </p>
                      {data.deliveryMethod !== 'link' && (
                        <p className="text-gray-700">
                          <span className="font-medium">
                            {data.deliveryMethod === 'email' ? 'Email:' :
                             data.deliveryMethod === 'sms' ? 'Phone:' :
                             'WhatsApp:'}
                          </span> {data.recipientContact}
                        </p>
                      )}
                      <p className="text-gray-700">
                        <span className="font-medium">From:</span> {data.senderName}
                      </p>
                      <p className="text-gray-700">
                        <span className="font-medium">Delivery:</span> {
                          data.deliveryMethod === 'email' ? '📧 Email' :
                          data.deliveryMethod === 'sms' ? '📱 Text Message' :
                          data.deliveryMethod === 'whatsapp' ? '💬 WhatsApp' :
                          '🔗 Shareable Link'
                        }
                      </p>
                      {data.includesCoffee && (
                        <p className="text-gray-700">
                          <span className="font-medium">Gift:</span> ${data.coffeeAmount} Coffee
                        </p>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={handleSend}
                    disabled={isSending}
                    className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-3"
                  >
                    {isSending ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {data.deliveryMethod === 'link' ? 'Creating your link...' : 'Sending birthday joy...'}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {data.deliveryMethod === 'link' ? 'Create Birthday Card' : 'Send Birthday Card'}
                        <Sparkles className="w-5 h-5" />
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => setCurrentStep(1)}
                    className="w-full py-3 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Start over
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Skip recap for steps 1-4 */}
        {currentStep > 1 && currentStep <= 4 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 text-center"
          >
            <div className="inline-flex items-center gap-2 text-sm text-gray-500 bg-white/80 backdrop-blur px-4 py-2 rounded-full">
              {currentStep > 1 && data.recipientName && (
                <span>For {data.recipientName}</span>
              )}
              {currentStep > 2 && data.contactInfo && (
                <>
                  <span>•</span>
                  <span>{data.contactInfo}</span>
                </>
              )}
              {currentStep > 3 && data.senderName && (
                <>
                  <span>•</span>
                  <span>From {data.senderName}</span>
                </>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default StepByStepBirthdayFlow;